import { useEffect, useState, useRef, useCallback } from 'react';

// Hook pro sledování pozice scrollu s optimalizací výkonu
export const useScrollAnimation = () => {
  const [scrollY, setScrollY] = useState(0);
  const ticking = useRef(false);

  const updateScrollY = useCallback(() => {
    setScrollY(window.pageYOffset);
    ticking.current = false;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(updateScrollY);
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [updateScrollY]);

  return { scrollY };
};

// Hook pro parallax efekty s optimalizací výkonu
export const useParallax = (speed = 0.5) => {
  const [offset, setOffset] = useState(0);
  const ticking = useRef(false);

  const updateParallax = useCallback(() => {
    const scrolled = window.pageYOffset;
    setOffset(scrolled * speed);
    ticking.current = false;
  }, [speed]);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(updateParallax);
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [updateParallax]);

  return offset;
};

// Hook pro animace při scrollování do view
export const useIntersectionObserver = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated) {
        setIsVisible(true);
        setHasAnimated(true);
      }
    }, {
      threshold: 0.1,
      rootMargin: '50px',
      ...options
    });

    const element = ref.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [hasAnimated, options]);

  return [ref, isVisible];
};

// Optimalizovaný hook pro animace karet
export const useCardAnimation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2, rootMargin: '20px' }
    );

    const element = ref.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  return { ref, isVisible };
};

// Hook pro smooth scroll reveal s pokročilými efekty
export const useSmoothReveal = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
    fadeDirection = 'up' // 'up', 'down', 'left', 'right', 'scale'
  } = options;

  const [revealState, setRevealState] = useState({
    isVisible: false,
    progress: 0
  });
  const ref = useRef();

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const progress = Math.min(Math.max(entry.intersectionRatio / threshold, 0), 1);
        
        setRevealState({
          isVisible: entry.isIntersecting,
          progress: progress
        });

        if (triggerOnce && entry.isIntersecting) {
          observer.unobserve(element);
        }
      },
      { 
        threshold: Array.from({length: 11}, (_, i) => i * 0.1),
        rootMargin 
      }
    );

    observer.observe(element);

    return () => observer.unobserve(element);
  }, [threshold, rootMargin, triggerOnce]);

  // Vypočítání transform hodnot na základě směru a progress
  const getTransform = () => {
    const { progress } = revealState;
    const translateValue = (1 - progress) * 50; // 50px max translate
    const scaleValue = 0.8 + (progress * 0.2); // scale from 0.8 to 1
    
    switch (fadeDirection) {
      case 'up':
        return `translateY(${translateValue}px)`;
      case 'down':
        return `translateY(-${translateValue}px)`;
      case 'left':
        return `translateX(${translateValue}px)`;
      case 'right':
        return `translateX(-${translateValue}px)`;
      case 'scale':
        return `scale(${scaleValue})`;
      default:
        return `translateY(${translateValue}px)`;
    }
  };

  const animationStyle = {
    opacity: revealState.isVisible ? 1 : 0,
    transform: getTransform(),
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
  };

  return { ref, ...revealState, animationStyle };
};
